import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronDown, AlertCircle } from 'lucide-react';

const TimePicker = ({ value, onChange, error, label, required, selectedDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const pickerRef = useRef(null);

  // Parse initial value if provided
  useEffect(() => {
    if (value) {
      const [hour, minute] = value?.split(':');
      setSelectedHour(hour);
      setSelectedMinute(minute);
    }
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef?.current && !pickerRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Helper function to check if a time is in the past
  const isTimePast = (hour, minute) => {
    if (!selectedDate) return false;

    const now = new Date();
    const selectedDateTime = new Date(selectedDate);
    
    // Only disable times if the selected date is today
    const isToday = 
      selectedDateTime?.getDate() === now?.getDate() &&
      selectedDateTime?.getMonth() === now?.getMonth() &&
      selectedDateTime?.getFullYear() === now?.getFullYear();

    if (!isToday) return false;

    // Create a time object for comparison
    const currentHour = now?.getHours();
    const currentMinute = now?.getMinutes();
    const timeHour = parseInt(hour, 10);
    const timeMinute = parseInt(minute, 10);

    // Check if the time is in the past
    if (timeHour < currentHour) return true;
    if (timeHour === currentHour && timeMinute <= currentMinute) return true;

    return false;
  };

  // Generate hours (00-23)
  const hours = Array.from({ length: 24 }, (_, i) => String(i)?.padStart(2, '0'));
  
  // Generate minutes in 5-minute intervals (00, 05, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5)?.padStart(2, '0'));

  const handleHourSelect = (hour) => {
    // Don't allow selection of past hours
    if (isTimePast(hour, '00')) return;
    
    setSelectedHour(hour);
    // If minute is already selected, update the time and close
    if (selectedMinute) {
      // Check if the combination is valid
      if (!isTimePast(hour, selectedMinute)) {
        const newTime = `${hour}:${selectedMinute}`;
        onChange(newTime);
        setIsOpen(false);
      }
    }
  };

  const handleMinuteSelect = (minute) => {
    // Don't allow selection if combined time is in the past
    if (selectedHour && isTimePast(selectedHour, minute)) return;
    
    setSelectedMinute(minute);
    // If hour is already selected, update the time and close
    if (selectedHour) {
      const newTime = `${selectedHour}:${minute}`;
      onChange(newTime);
      setIsOpen(false);
    }
  };

  const displayValue = value ? value : 'Select time';

  return (
    <div className="space-y-2 relative" ref={pickerRef}>
      {label && (
        <label className="block text-sm font-semibold text-gray-900">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {/* Time Display Block - Clickable - FIXED: Exact height match with date picker */}
      <div className="relative h-[46px]">
        <div
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full h-[46px] px-4 py-3 text-base rounded-lg bg-white border-2 cursor-pointer transition-all duration-200 flex items-center justify-between ${
            error 
              ? 'border-red-300 focus:ring-red-500' 
              : isOpen 
              ? 'border-blue-500 ring-2 ring-blue-500' :'border-gray-300 hover:border-blue-400'
          }`}
        >
          <div className="flex items-center gap-2">
            <Clock className={`w-5 h-5 ${value ? 'text-blue-600' : 'text-gray-400'}`} />
            <span className={value ? 'text-gray-900' : 'text-gray-400'}>
              {displayValue}
            </span>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {error && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
      {/* Time Picker Dropdown */}
      {isOpen && (
        <div className="absolute z-50 bottom-full mb-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            {/* Hours Column */}
            <div className="overflow-y-auto max-h-64">
              <div className="p-2 bg-gray-50 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 text-center">Hours</p>
              </div>
              <div className="p-2 space-y-1">
                {hours?.map((hour) => {
                  const isPast = isTimePast(hour, '00');
                  return (
                    <button
                      key={hour}
                      onClick={() => handleHourSelect(hour)}
                      disabled={isPast}
                      className={`w-full px-3 py-2 text-sm rounded-md transition-colors duration-150 ${
                        isPast
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50' :selectedHour === hour ?'bg-blue-500 text-white font-semibold' :'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {hour}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Minutes Column */}
            <div className="overflow-y-auto max-h-64">
              <div className="p-2 bg-gray-50 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-700 text-center">Minutes</p>
              </div>
              <div className="p-2 space-y-1">
                {minutes?.map((minute) => {
                  const isPast = selectedHour && isTimePast(selectedHour, minute);
                  return (
                    <button
                      key={minute}
                      onClick={() => handleMinuteSelect(minute)}
                      disabled={isPast}
                      className={`w-full px-3 py-2 text-sm rounded-md transition-colors duration-150 ${
                        isPast
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50' :selectedMinute === minute ?'bg-blue-500 text-white font-semibold' :'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {minute}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Helper Text */}
          <div className="p-2 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-center text-gray-600">
              {!selectedHour && !selectedMinute && 'Select hour and minute'}
              {selectedHour && !selectedMinute && 'Now select minute'}
              {!selectedHour && selectedMinute && 'Now select hour'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;